import { convertAnnotationsToContent, getSubject } from '@ethsign/pdf-editor';
import { ethSignAesDecrypt } from '@ethsign/utils-web';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';

import { ContractType, Recipient } from '@/types';

import { convertXmlToJSON } from './webviewer';

export function fileToString(file?: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (fileEvent) => {
      const buffer = Buffer.from(fileEvent.target?.result as ArrayBuffer);
      resolve(buffer.toString('base64'));
    };
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    reader.readAsArrayBuffer(file!);
  });
}

export function stringToArrayBuffer(content: string): ArrayBuffer {
  const buffer = Buffer.from(content, 'base64');
  return buffer;
}

export function stringToFile(content: string, fileName?: string): File {
  const buffer = Buffer.from(content, 'base64');
  const blob = new Blob([buffer]);
  const file = new File([blob], fileName || '', { type: 'application/pdf' });
  return file;
}

export function fileToBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (fileEvent) => {
      resolve(fileEvent.target?.result as ArrayBuffer);
    };
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    reader.readAsArrayBuffer(file);
  });
}

export function fileToBinaryString(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (fileEvent) => {
      resolve(fileEvent.target?.result as string);
    };
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    reader.readAsBinaryString(file);
  });
}

export const BufferToString = (buffer: ArrayBuffer): string => {
  const arr = new Uint8Array(buffer);
  return JSON.stringify(arr);
};

export const BufferToFile = (buffer: ArrayBuffer, fileName?: string): File => {
  const arr = new Uint8Array(buffer);
  const blob = new Blob([arr]);
  const file = new File([blob], fileName || '', { type: 'application/pdf' });
  return file;
};

export function download(data: ArrayBuffer, fileName: string, mimeType?: string): void {
  const arr = new Uint8Array(data);
  const blob = new Blob([arr], { type: mimeType || 'application/pdf' });
  const file = new File([blob], fileName || '', { type: mimeType || 'application/pdf' });
  const objectUrl = URL.createObjectURL(file);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  window.URL.revokeObjectURL(link.href);
}

export const getDecryptedFile = async (
  fileContent: string,
  password?: string
): Promise<{ fileString: string; annotation: string }> => {
  const fileStr = password ? await ethSignAesDecrypt(fileContent, password) : fileContent;
  const fileRes = JSON.parse(fileStr);
  return fileRes;
};

// add annotations of signature to pdf
export const addAnnotationToPDF = async (
  fileContent: string,
  annotations: string[],
  subject: Record<string, any>
): Promise<any> => {
  const newAnnotations = annotations.reduce((acc: any, item: any) => {
    return item ? acc.concat(JSON.parse(item)) : acc;
  }, []);
  return convertAnnotationsToContent({
    annotations: newAnnotations,
    pdf: fileContent,
    subject
    // font: 'https://sign-public-cdn.s3.us-east-1.amazonaws.com/signature-next/fonts/PingFang-SC-Regular_240905090256.ttf'
  });
};

export const getPDFSubject = async (
  file: File
): Promise<{
  id: string;
  enableEncryption: boolean;
} | null> => {
  try {
    const buffer = await fileToBuffer(file);

    const subjectStr = await getSubject(buffer);
    const subject = subjectStr ? JSON.parse(subjectStr) : null;
    return subject;
  } catch (e) {
    console.log(e);
    throw Error('PDF verify failed');
    return null;
  }
};

export const getReadOnlyTextXml = async (annotation: string): Promise<string> => {
  if (!annotation) return '';
  const data = JSON.parse(await convertXmlToJSON(annotation)).filter((x: any) => x.data.type === 'readonlyText');
  return data?.length > 0 ? JSON.stringify(data) : '';
};

export const getFileExtension = (fileName: string): string => {
  if (!fileName) return '';
  let ext = '';
  const i = fileName.lastIndexOf('.');
  if (i > -1) {
    ext = fileName.substring(i + 1);
  }
  return ext;
};

export function renderSize(filesize: number): string {
  if (!filesize) return '';
  const unitArr = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  let index = 0;
  const srcsize = filesize;
  index = Math.floor(Math.log(srcsize) / Math.log(1000));
  let size = srcsize / Math.pow(1000, index);
  size = Number(size.toFixed(2)); //保留的小数位数
  return size + unitArr[index];
}

export const composePDFByAnnotation = async ({
  fileContent,
  password,
  data,
  signedData,
  contractType
}: {
  signedData: any[];
  fileContent: string;
  password?: string;
  data: {
    id: string;
    enableEncryption: boolean;
  };
  contractType?: ContractType;
}): Promise<any> => {
  let decryptedFile: any;
  try {
    decryptedFile = await getDecryptedFile(fileContent, password);
  } catch (error) {
    console.log(error);
    throw Error('Password is wrong!');
  }
  const annotations = await Promise.all(
    signedData.map(async (x: any) => {
      const decrypted = password ? await ethSignAesDecrypt(x, password) : x;
      return await convertXmlToJSON(decrypted);
    })
  );

  if (contractType !== ContractType.link_sign_contract) {
    const annotationXml = await getReadOnlyTextXml(decryptedFile.annotation);
    annotationXml && annotations.push(annotationXml);
  }
  // version = 1: 增加合成readOnly text
  const documentBuffer = await addAnnotationToPDF(decryptedFile.fileString, annotations, {
    id: data.id,
    enableEncryption: data.enableEncryption,
    version: 1
  });
  return documentBuffer;
};

export const composeZipByPDF = (name: string, data: any[]): void => {
  const zip = new JSZip();
  data.forEach((it) => {
    zip.file(it.name, new Uint8Array(it.file));
  });

  zip.generateAsync({ type: 'blob' }).then(function (content) {
    saveAs(content, name);
  });
};

export const downloadContractFile = async ({
  recipients,
  contractType,
  fileContent,
  password,
  data
}: {
  recipients: Recipient[];
  contractType: ContractType;
  fileContent: string;
  password?: string;
  data: {
    id: string;
    enableEncryption: boolean;
    title: string;
  };
}): Promise<boolean> => {
  const signers = recipients.filter((x: any) => x.role === 'signer' && x.status === 'signed');

  if (contractType === ContractType.visual_contract) {
    const signedData = signers.map((x) => {
      return {
        contractID: x.contractId!,
        content: JSON.parse(x.signingContent)?.signingContent,
        fileName: `${x.nickName}${x.nickName ? '_' : ''}${x.address}_${x.chainType}`
      };
    });
    const pdfFileList = await Promise.all(
      signedData.map(async (it: { contractID: string; content: any; fileName: string }) => {
        return {
          name: `${it.fileName}.pdf`,
          file: await composePDFByAnnotation({
            fileContent,
            signedData: [it.content],
            password,
            data: {
              ...data,
              id: it.contractID
            }
          })
        };
      })
    );
    composeZipByPDF(data.title, pdfFileList);
    return true;
  }

  const signedData = signers.map((x: any) => JSON.parse(x.signingContent)?.signingContent);

  const pdfBuffer = await composePDFByAnnotation({
    fileContent,
    signedData,
    password,
    data,
    contractType
  });

  download(pdfBuffer, `${data.title}.pdf`, 'application/pdf');
  return true;
};

export function downloadByUrl(url: string, fileName: string, cb?: () => void): void {
  fetch(url)
    .then((response) => {
      return response.blob();
    })
    .then((blob) => {
      const aTag = document.createElement('a');
      aTag.download = fileName;
      // aTag.target = '__blank';
      aTag.href = URL.createObjectURL(blob);
      aTag.click();
      cb && cb();
    })
    .catch(() => {
      cb && cb();
    });
}
